/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { AssessmentContext } from 'app/core/lib/assessment-context';
import { IQuestionComponent, IQuestionSavedResponse, IQuestionConfig } from 'app/core/components/assessments/interfaces';
import { Api, apiCall } from 'app/shared/lib/api/api';
import { CarePlanService } from 'app/cm/services/care-plan-service';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { QuestionBase } from 'app/core/components/assessments/questions/question-base';
import { getReference } from 'app/shared/models/essette-types';
import * as CM from 'app/cm/models/essette-api';


@autoinject
export class QuestionSuggestGoalCustomElement extends QuestionBase implements IQuestionComponent {

    ////****************** Fields and Properties **************************************////
    private caseId: string | null = null;
    protected goalDefinition: CM.CarePlanGoalDefinition | null = null;
    protected caseGoals: CM.CarePlanGoal[] = [];
    protected addGoal: boolean = false;


    ////****************** Lifecycle events (in order of execution) ******************////
    constructor(
        protected readonly context: AssessmentContext,
        private readonly api: Api,
        private readonly service: CarePlanService,
        private readonly logger: AppLogger) {

        super(context);
        this.logger.source = this;

        if(!context || !context.currentSession) {
            throw new Error("No Assessment context is available");
        }

        if(!this.context.questionComponents.includes(this)) {
            this.context.questionComponents.push(this);
        }
    }

    protected async activate(config: IQuestionConfig): Promise<void> {
        if(this.context?.attachment?.attachedTo?.type?.code !== 'CM.Case') {
            throw new Error('Invalid question type loaded for attachment. Expected attachment target type of CM.Case')
        }
        this.question = config.question;
        this.required = !!this.question.flag?.isRequired;
        this.showHistory = config.showHistory;
        this.readonly = config.readonly;
        this.isValid = !this.required;
        this.caseId = this.context?.attachment?.attachedTo?.id || null;

        this.loadData();
    }


    ////****************** Events ***********************************************////


    ////****************** Functions ***********************************************////
    private async loadData(): Promise<void> {
        if(!this.question) {
            throw new Error('Configuration missing required property: [question]');
        }
        if(!this.caseId) {
            throw new Error(`Unable to find caseId on context's attachment`);
        }

        const goalDefinitionId = String(this.question.relatedRecordID || null);
        this.loadGoalDefinition(goalDefinitionId);
        this.findMatchingGoalsOnCase(this.caseId, goalDefinitionId);
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadGoalDefinition(goalDefinitionId: string): Promise<void> {
        try {
            this.state.beginLoad();
            const response = await this.service.loadGoalDefinition(goalDefinitionId);
            this.goalDefinition = response.results[0];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async findMatchingGoalsOnCase(caseId: string, goalDefinitionId: string): Promise<void> {
        const options: Partial<IQueryLoadOptions> = { all: true };
        const includes: string[] = ['metadata.completed.byUser', 'metadata.created.byUser'];

        try {
            this.state.beginLoad();
            const query = this.api
                .query(`cm/cases/${caseId}/careplan/goals`)
                .applyStandardLoadOptions(options)
                .include(...includes)
                .filterIdentifier('definition.id', goalDefinitionId);

            const response = await this.api.get(query);
            this.caseGoals = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }

    }

    @apiCall
    private async addGoalToCase(goal: CM.CarePlanGoal): Promise<boolean> {
        try {
            this.state.beginSave();
            const response = await this.service.insertCarePlanGoal(goal);

            if(response.isSuccessful) {
                this.addGoal = false;
                this.loadData();
            }

            return response.isSuccessful;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }


    ////****************** IQuestionComponent ****************************************************////
    public async saveAnswer(session?: CM.CaseAssessmentSession): Promise<IQuestionSavedResponse> {
        const saveResponse: IQuestionSavedResponse = {
            question: this.question,
            isSuccessful: true
        };

        if(this.addGoal) {
            const newGoal: CM.CarePlanGoal = {
                definition: getReference(this.goalDefinition),
                case: { id: this.caseId }
            };

            saveResponse.isSuccessful = await this.addGoalToCase(newGoal);
        }

        return saveResponse;
    }

    public getAnswers(): CM.CaseAssessmentAnswer[] {
        return [];
    }

}
